import { useState } from "react";
import { format } from "date-fns";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { Clock, X, RotateCcw } from "lucide-react";
import DeleteConfirmationDialog from "@/components/shared/DeleteConfirmationDialog";

interface PendingInvitation {
  id: string;
  email: string;
  created_at: string;
  status: string;
}

interface PendingInvitationRowProps {
  invitation: PendingInvitation;
  canManageMembers: boolean;
  onResendInvitation: (invitationId: string, email: string) => void;
  onCancelInvitation: (invitationId: string, email: string) => void;
}

const PendingInvitationRow = ({ 
  invitation, 
  canManageMembers, 
  onResendInvitation, 
  onCancelInvitation 
}: PendingInvitationRowProps) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = () => {
    onCancelInvitation(invitation.id, invitation.email);
    setShowCancelDialog(false);
  };

  return (
    <>
      <TableRow className="bg-yellow-50">
        <TableCell className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback>
              <Clock className="h-4 w-4" />
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium text-gray-600">
              {invitation.email}
            </div>
            <div className="text-xs text-gray-500">
              Invited {format(new Date(invitation.created_at), 'dd MMMM yyyy')}
            </div>
          </div>
        </TableCell>
        <TableCell className="hidden sm:table-cell">
          <div className="text-sm text-gray-600">
            {invitation.email}
          </div>
        </TableCell>
        <TableCell className="hidden md:table-cell">
          {/* Company Role - empty for pending */}
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          {/* App User Type - empty for pending */}
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="flex items-center gap-1 w-fit">
            <Clock className="h-3 w-3" />
            Pending
          </Badge>
        </TableCell>
        {canManageMembers && (
          <TableCell className="text-right">
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onResendInvitation(invitation.id, invitation.email)}
                title="Resend invitation"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelClick}
                title="Cancel invitation"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </TableCell>
        )}
      </TableRow>

      <DeleteConfirmationDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel Invitation"
        description="Are you sure you want to cancel the invitation for"
        itemName={invitation.email}
      />
    </>
  );
};

export default PendingInvitationRow;
